import { ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../../redux/slices/accountSlice";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { Colors, addAlpha } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { DataGraphContainer } from "./DataGraphContainer";
import { useNavigate } from "../../hooks/useNavigate";
import { NavigationId } from "../../navigation/AppParamList";
import TextingPanelUsage from "../CreatorDashboard/TextingPanelUsage";
import {
  getConversationState,
  getUnreadConversationsQuery,
} from "../../redux/slices/conversationSlice";
import { getDocs } from "../../firebase";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import { ConversationItem } from "../DisplayItem/ConversationItem";
import { MixpanelContext } from "../../context/AnalyticsService";
import TutorialManager from "./TutorialManager";
import RecentProductsSection from "./RecentProductsSection";
import { RevenueGraphContainer } from "./RevenueGraphContainer";
import { onCreatorPanelNavigatePath } from "../../utils/navigationUtils";
import { greetingMessage } from "@markit/common.utils";
import MarkitPlusActionButton from "../Subscription/MarkitPlusActionButton";

type PeoplePanelProps = {
  justFinishedOnboarding?: boolean;
};

export const PeoplePanel = (props: PeoplePanelProps) => {
  const { justFinishedOnboarding } = props;
  const { accountData, followingAccountData, followersData } =
    useSelector(getAccountSlice);
  const { userConversations } = useSelector(getConversationState);
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const styles = {
    overallContainerView: {
      borderRadius: 20,
      paddingBlock: 14,
      backgroundColor: Colors.WHITE,
      minHeight: 225,
    },
    sectionHeaderText: {
      marginBottom: 14,
      fontSize: 16,
      fontWeight: 600,
      whiteSpace: "nowrap",
      color: Colors.GRAY1,
    },
  };

  // get the number of unread conversation messages
  useAsyncOnMount(async () => {
    const unreadQuery = await getUnreadConversationsQuery(accountData.uid);
    const unreadConversations = await getDocs(unreadQuery);
    setUnreadMessages(unreadConversations.size);
  });

  const recentConversations = useMemo(
    () => userConversations.slice(0, 3),
    [userConversations]
  );

  const sendConversation = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Pressed Home Main Action", {
        distinct_id: accountData.uid,
        action: "Send Individual Chat",
        platform: "webapp",
      });
    }
    onCreatorPanelNavigatePath(navigate, NavigationId.HOME_CONVERSATIONS);
  }, [mixpanel, navigate, accountData.uid]);

  const createProductOnPress = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Pressed Home Main Action", {
        distinct_id: accountData.uid,
        action: "Create a Product",
        platform: "webapp",
      });
    }
    navigate(NavigationId.NEW_PRODUCT);
  }, [mixpanel, navigate, accountData.uid]);

  const sendTextOnPress = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Pressed Home Main Action", {
        distinct_id: accountData.uid,
        action: "Send a Text",
        platform: "webapp",
      });
    }
    onCreatorPanelNavigatePath(navigate, NavigationId.HOME_TEXTING);
  }, [mixpanel, navigate, accountData.uid]);

  const createInstagramAutomationOnPress = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Pressed Home Main Action", {
        distinct_id: accountData.uid,
        action: "Create DM Automation",
        platform: "webapp",
      });
    }
    navigate(NavigationId.HOME_AUTO_DM);
  }, [mixpanel, navigate, accountData.uid]);

  const renderMainActionItem = useCallback(
    (
      title: string,
      subtext: string,
      iconName: string,
      accentColor: string,
      onPress: () => void,
      isLast?: boolean
    ) => (
      <div
        style={{
          flex: "1 1 0%",
          minWidth: 0,
          boxSizing: "border-box",
          paddingInline: 14,
          borderRight: isLast ? "none" : `0.5px solid ${Colors.GRAY2}`,
          cursor: "pointer",
        }}
        onClick={onPress}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.6")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        <div className="AlignedRow" style={{ gap: 14 }}>
          <div
            className="Centering"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              backgroundColor: addAlpha(accentColor, 0.12),
              border: `1px solid ${accentColor}`,
              flexShrink: 0,
            }}
          >
            <Icon icon={iconName} height={22} style={{ color: accentColor }} />
          </div>
          <div className="ColumnNormal" style={{ gap: 2 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <span className="bodyMedium">{title}</span>
              <Icon
                icon="ion:chevron-right"
                height={10}
                style={{ color: Colors.GRAY2, flexShrink: 0, marginBottom: -2 }}
              />
            </div>
            <span className="smallBodySubtext">{subtext}</span>
          </div>
        </div>
      </div>
    ),
    []
  );

  const renderCardItem = useCallback(
    (title: string, children: ReactNode, onPress: () => void) => (
      <div style={{ flex: 1, minWidth: 0 }}>
        <StandardBorderedContainer
          containerStyles={{
            ...styles.overallContainerView,
            height: 225,
            width: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <div className="ColumnNormalSelect" style={{ height: "100%" }}>
            <div
              className="AlignedRowSelect"
              style={{ gap: 7, paddingInline: 14 }}
              onClick={onPress}
            >
              <span className="bodyMedium">{title}</span>
              <Icon
                icon="ion:chevron-right"
                height={12}
                style={{ color: Colors.BLACK }}
              />
            </div>
            <HorizontalDivider altMargin={14} />
            <div className="ColumnNormal" style={{ flex: 1, minHeight: 0 }}>
              {children}
            </div>
          </div>
        </StandardBorderedContainer>
      </div>
    ),
    [styles.overallContainerView]
  );

  return (
    <div className="ColumnNormal" style={{ gap: 24 }}>
      {/** For if some external service goes down (ie. Firebase or AWS) */}
      {/* <GlobalOutageBanner /> */}
      <StandardBorderedContainer
        containerStyles={{
          borderRadius: 20,
          backgroundColor: Colors.WHITE,
          padding: 14,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <div className="AlignedRowSpaced">
            <span style={{ fontSize: 32, fontWeight: 600 }}>
              {justFinishedOnboarding
                ? "You're all set up! 🥳"
                : greetingMessage()}
            </span>
            <MarkitPlusActionButton mainAction />
          </div>
          <div className="AlignedRow" style={{ gap: 24 }}>
            {renderCardItem("Your Audience", <DataGraphContainer />, () =>
              navigate(NavigationId.HOME_CONTACTS)
            )}
            {renderCardItem("Your Revenue", <RevenueGraphContainer />, () =>
              navigate(NavigationId.HOME_PAYOUTS)
            )}
          </div>
        </div>
      </StandardBorderedContainer>
      <StandardBorderedContainer
        containerStyles={{
          borderRadius: 20,
          backgroundColor: Colors.WHITE,
          paddingBlock: 14,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div className="AlignedRow">
          {renderMainActionItem(
            "Send a Text",
            "Text your audience in seconds",
            "ion:paper-plane",
            Colors.BLUE5,
            sendTextOnPress
          )}
          {renderMainActionItem(
            "Send Individual Chat",
            "Quickly message someone one-on-one",
            "ion:chatbubbles",
            Colors.ORANGE1,
            sendConversation
          )}
          {renderMainActionItem(
            "Create DM Automation",
            "Convert your IG followers into text subscribers",
            "ion:flash",
            Colors.PURPLE12,
            createInstagramAutomationOnPress
          )}
          {renderMainActionItem(
            "Create a Product",
            "Forms, newsletters, products, events & more",
            "ion:bag-handle",
            Colors.PINK2,
            createProductOnPress,
            true
          )}
        </div>
      </StandardBorderedContainer>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 24 }}>
        <div className="ColumnNormal" style={{ flex: 1, minWidth: 0, gap: 24 }}>
          <RecentProductsSection />
          <StandardBorderedContainer
            containerStyles={{
              ...styles.overallContainerView,
              width: "100%",
              boxSizing: "border-box",
              paddingBottom: userConversations.length > 0 ? 0 : 14,
            }}
            onPress={() => navigate(NavigationId.HOME_CONVERSATIONS)}
          >
            <div className="ColumnNormalSelect">
              <div
                className="AlignedRowSpaced"
                style={{ paddingInline: 14, paddingBottom: 14 }}
              >
                <div
                  className="AlignedRow"
                  style={{ gap: 7 }}
                  onClick={() => navigate(NavigationId.HOME_CONVERSATIONS)}
                >
                  <span className="bodyMedium">Conversations</span>
                  <Icon
                    icon="ion:chevron-right"
                    height={12}
                    style={{ color: Colors.BLACK }}
                  />
                </div>
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <span className="bodySmallMedium">
                    Unread ({unreadMessages})
                  </span>
                  {unreadMessages > 0 ? (
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        backgroundColor: Colors.BLUE5,
                        borderRadius: 100,
                        marginTop: 2,
                      }}
                    />
                  ) : null}
                </div>
              </div>
              <HorizontalDivider altMargin={0} />
              {userConversations.length > 0 ? (
                recentConversations.map((conversation, index) => {
                  const participant = followingAccountData.find(
                    (user) => user.uid === conversation.uid
                  );
                  const followerData = followersData.find(
                    (follower) => follower.uid === conversation.uid
                  );
                  return (
                    <div key={conversation.uid}>
                      <div style={{ padding: "10px 14px", paddingBottom: 10 }}>
                        <ConversationItem
                          item={conversation}
                          participant={participant}
                          followerData={followerData}
                          contentContainerStyles={{
                            width: "100%",
                            height: 53,
                          }}
                        />
                      </div>
                      {index !== recentConversations.length - 1 ? (
                        <HorizontalDivider altMargin={0} />
                      ) : null}
                    </div>
                  );
                })
              ) : (
                <EmptyStateButton
                  title="No Unread Conversations"
                  description="Conversations are created when people respond to your texts"
                  icon={<Icon icon="ion:chatbubbles" height={35} />}
                  containerStyles={{ marginTop: 36 }}
                />
              )}
            </div>
          </StandardBorderedContainer>
        </div>
        <div className="ColumnNormal" style={{ flex: 1, minWidth: 0, gap: 24 }}>
          <TextingPanelUsage fullView />
          <TutorialManager />
        </div>
      </div>
    </div>
  );
};
